<nb-card class="credits-info-dialog">
  <nb-card-header>
    <div>
      <h4>{{ 'pages.organisation.credits.info.title' | translate }}</h4>
      <p class="subtitle">{{ 'pages.organisation.credits.info.subtitle' | translate }}</p>
    </div>
    <button nbButton ghost status="basic" size="small" type="button" (click)="close()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body>
    <div *ngIf="loading" class="ci-status">
      <nb-icon icon="loader-outline"></nb-icon>
      <span>{{ 'pages.organisation.credits.info.loading' | translate }}</span>
    </div>

    <div *ngIf="error && !loading" class="ci-status ci-status--error">
      {{ 'pages.organisation.credits.info.error' | translate }}
    </div>

    <ng-container *ngIf="!loading && !error && pricing">
      <!-- 1. One sentence, then straight to the calculator -->
      <p class="ci-lead">{{ 'pages.organisation.credits.info.lead' | translate }}</p>

      <!-- 2. Three cases, seeded from what we know about the customer -->
      <p class="ci-basis" *ngIf="basis">
        <nb-icon icon="info-outline"></nb-icon>
        <span *ngIf="basisSource; else noLms">{{
          'pages.organisation.credits.info.basisFromLms'
            | translate
              : {
                  lms: ('pages.organisation.credits.info.lms.' + basisSource | translate),
                  learners: (knownLearners | number),
                  courses: (basis.courses?.count ?? 0 | number),
                  agents: basis.agents.length
                }
        }}</span>
        <ng-template #noLms>{{
          'pages.organisation.credits.info.basisNoLms'
            | translate: { courses: (basis.courses?.count ?? 0 | number), agents: basis.agents.length }
        }}</ng-template>
        <span *ngIf="basis.learners_active_30d !== null">
          {{
            'pages.organisation.credits.info.basisActive'
              | translate: { active: (basis.learners_active_30d | number) }
          }}
        </span>
        <span *ngIf="knownCoursesBeyond">
          {{
            'pages.organisation.credits.info.basisKnownCourses'
              | translate: { known: (knownCoursesBeyond | number) }
          }}
        </span>
      </p>

      <div class="ci-scenarios" role="radiogroup" [attr.aria-label]="'pages.organisation.credits.info.scenarios' | translate">
        <button
          type="button"
          role="radio"
          class="ci-scenario"
          *ngFor="let sc of scenarios; trackBy: trackScenario"
          [class.al-pressed]="sc === active"
          [attr.aria-checked]="sc === active"
          (click)="select(sc)"
        >
          <span class="ci-scenario__name">{{ 'pages.organisation.credits.info.scenario.' + sc.key | translate }}</span>
          <strong class="ci-scenario__credits">{{ totalOf(sc) | number: '1.0-0' }}</strong>
          <span class="ci-scenario__eur" *ngIf="pricing.credits_per_eur">
            {{ 'pages.organisation.credits.info.eurPerMonth' | translate: { eur: (eurOf(totalOf(sc)) | number: '1.0-0') } }}
          </span>
        </button>
      </div>
      <div class="ci-scenario-bar">
        <span class="ci-note">{{ 'pages.organisation.credits.info.scenarioHint.' + active.key | translate }}</span>
        <button nbButton ghost size="tiny" status="primary" type="button" (click)="resetActive()">
          <nb-icon icon="refresh-outline"></nb-icon>
          {{ 'pages.organisation.credits.info.resetScenario' | translate }}
        </button>
      </div>

      <!-- 3. One block per agent: model + its usage -->
      <section class="tile-flat ci-agent" *ngFor="let agent of active.agents; let i = index; trackBy: trackAgent">
        <header class="tile-flat__header ci-agent__head">
          <h5>{{ agent.name || ('pages.organisation.credits.info.agent' | translate: { n: i + 1 }) }}</h5>
          <span class="ci-agent__sum">
            {{ agentCredits(agent) | number: '1.0-0' }}
            {{ 'pages.organisation.credits.info.creditsShort' | translate }}
            <ng-container *ngIf="pricing.credits_per_eur">
              · {{ eurOf(agentCredits(agent)) | number: '1.2-2' }} €
            </ng-container>
          </span>
          <button
            *ngIf="active.agents.length > 1"
            nbButton
            ghost
            size="tiny"
            status="basic"
            type="button"
            [nbTooltip]="'pages.organisation.credits.info.removeAgent' | translate"
            (click)="removeAgent(agent)"
          >
            <nb-icon icon="trash-2-outline"></nb-icon>
          </button>
        </header>

        <div class="ci-model" *ngIf="models.length">
          <label class="label">{{ 'pages.organisation.credits.info.model' | translate }}</label>
          <nb-select fullWidth size="small" [(selected)]="agent.provider" [optionsWidth]="460">
            <nb-option *ngFor="let m of models; trackBy: trackModel" [value]="m.provider">
              {{ m.label }} · {{ m.factor | number: '1.0-2' }}×
              <ng-container *ngIf="m.speed">
                · {{ 'pages.tutors.modelSelector.speed.' + m.speed | translate }}
              </ng-container>
            </nb-option>
          </nb-select>
        </div>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sLearners' | translate }}</span>
            <input
              class="ci-value"
              type="number"
              min="0"
              inputmode="numeric"
              [attr.aria-label]="'pages.organisation.credits.info.sLearners' | translate"
              [ngModel]="agent.learners"
              (ngModelChange)="setLearners(agent, $event)"
            />
          </div>
          <input
            type="range"
            min="0"
            [max]="agent.learnerRange"
            [step]="stepOf(agent.learnerRange)"
            [ngModel]="agent.learners"
            (ngModelChange)="setLearners(agent, $event)"
          />
          <div class="ci-range">
            <span class="ci-range__label">{{ 'pages.organisation.credits.info.range' | translate }}</span>
            <nb-button-group size="tiny" (valueChange)="setLearnerRange(agent, $event[0])">
              <button
                nbButtonToggle
                type="button"
                *ngFor="let r of learnerRanges"
                [value]="r"
                [pressed]="agent.learnerRange === r"
              >
                {{ r | number }}
              </button>
            </nb-button-group>
          </div>
        </div>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sQuestions' | translate }}</span>
            <strong>{{ agent.questionsPerLearner | number }}</strong>
          </div>
          <input type="range" min="0" max="100" step="5" [(ngModel)]="agent.questionsPerLearner" />
        </div>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sExercises' | translate }}</span>
            <strong>{{ agent.exercisesPerLearner | number }}</strong>
          </div>
          <input type="range" min="0" max="50" step="1" [(ngModel)]="agent.exercisesPerLearner" />
        </div>
      </section>

      <button
        *ngIf="active.agents.length < maxAgents"
        nbButton
        ghost
        size="small"
        status="primary"
        type="button"
        class="ci-add-agent"
        (click)="addAgent()"
      >
        <nb-icon icon="plus-outline"></nb-icon>
        {{ 'pages.organisation.credits.info.addAgent' | translate }}
      </button>

      <!-- Teaching material is made in the creator — not tied to an agent's model -->
      <section class="tile-flat">
        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sMaterials' | translate }}</span>
            <strong>{{ active.materialsPerMonth | number }}</strong>
          </div>
          <input type="range" min="0" max="50" step="1" [(ngModel)]="active.materialsPerMonth" />
        </div>
      </section>

      <!-- Courses as agent knowledge: set up once, kept current per month -->
      <section class="tile-flat ci-courses">
        <header class="tile-flat__header">
          <h5>{{ 'pages.organisation.credits.info.coursesTitle' | translate }}</h5>
        </header>
        <p class="ci-note">{{ 'pages.organisation.credits.info.coursesLead' | translate }}</p>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sCourses' | translate }}</span>
            <input
              class="ci-value"
              type="number"
              min="0"
              inputmode="numeric"
              [attr.aria-label]="'pages.organisation.credits.info.sCourses' | translate"
              [ngModel]="active.course.courses"
              (ngModelChange)="setCourses($event)"
            />
          </div>
          <input
            type="range"
            min="0"
            [max]="active.course.courseRange"
            [step]="stepOf(active.course.courseRange)"
            [ngModel]="active.course.courses"
            (ngModelChange)="setCourses($event)"
          />
          <div class="ci-range">
            <span class="ci-range__label">{{ 'pages.organisation.credits.info.range' | translate }}</span>
            <nb-button-group size="tiny" (valueChange)="setCourseRange($event[0])">
              <button
                nbButtonToggle
                type="button"
                *ngFor="let r of courseRanges"
                [value]="r"
                [pressed]="active.course.courseRange === r"
              >
                {{ r | number }}
              </button>
            </nb-button-group>
          </div>
        </div>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sUnits' | translate }}</span>
            <strong>{{ active.course.unitsPerCourse | number }}</strong>
          </div>
          <input type="range" min="0" max="50" step="1" [(ngModel)]="active.course.unitsPerCourse" />
        </div>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sMedia' | translate }}</span>
            <strong>{{ active.course.mediaPerCourse | number }}</strong>
          </div>
          <input type="range" min="0" max="200" step="5" [(ngModel)]="active.course.mediaPerCourse" />
        </div>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sVideoMinutes' | translate }}</span>
            <strong>{{ active.course.videoMinutesPerCourse | number }}</strong>
          </div>
          <input type="range" min="0" max="600" step="10" [(ngModel)]="active.course.videoMinutesPerCourse" />
        </div>

        <div class="ci-slider">
          <div class="ci-slider__head">
            <span>{{ 'pages.organisation.credits.info.sUpdateRate' | translate }}</span>
            <strong>{{ active.course.updateRatePct | number }} %</strong>
          </div>
          <input type="range" min="0" max="100" step="5" [(ngModel)]="active.course.updateRatePct" />
        </div>

        <div class="ci-course-sums">
          <div class="ci-course-sums__row">
            <span>{{ 'pages.organisation.credits.info.setupOnce' | translate }}</span>
            <strong>
              {{ courseSetupCredits | number: '1.0-0' }}
              {{ 'pages.organisation.credits.info.creditsShort' | translate }}
              <ng-container *ngIf="pricing.credits_per_eur">
                · {{ eurOf(courseSetupCredits) | number: '1.2-2' }} €
              </ng-container>
            </strong>
          </div>
          <div class="ci-course-sums__row">
            <span>{{
              'pages.organisation.credits.info.upkeepMonthly' | translate: { rate: active.course.updateRatePct }
            }}</span>
            <strong>
              {{ courseUpkeepCredits | number: '1.0-0' }}
              {{ 'pages.organisation.credits.info.creditsShort' | translate }}
              <ng-container *ngIf="pricing.credits_per_eur">
                · {{ eurOf(courseUpkeepCredits) | number: '1.2-2' }} €
              </ng-container>
            </strong>
          </div>
        </div>
        <p class="ci-note">{{ 'pages.organisation.credits.info.coursesNote' | translate }}</p>
      </section>

      <!-- 4. Result of the selected case: bar + legend + verdict -->
      <section class="tile-flat ci-result">
        <div class="ci-result__total">
          <span class="ci-result__label">{{
            'pages.organisation.credits.info.totalPerMonth' | translate
          }}</span>
          <strong class="ci-result__value">{{ totalCredits | number: '1.0-0' }}</strong>
          <span class="ci-result__unit">{{
            'pages.organisation.credits.info.creditsUnit' | translate
          }}</span>
        </div>
        <p class="ci-result__eur" *ngIf="pricing.credits_per_eur">
          {{
            'pages.organisation.credits.info.eurPerMonth'
              | translate: { eur: (totalEur | number: '1.2-2') }
          }}
        </p>

        <div class="ci-bar" *ngIf="totalCredits > 0">
          <span
            class="ci-bar__seg"
            *ngFor="let s of segments; trackBy: trackSegment"
            [style.width.%]="segmentPercent(s)"
            [style.background]="s.color"
            [nbTooltip]="
              ('pages.organisation.credits.info.seg.' + s.label | translate) +
              ': ' +
              (s.credits | number: '1.0-0')
            "
          ></span>
        </div>

        <div class="ci-legend">
          <span class="ci-legend__item" *ngFor="let s of segments; trackBy: trackSegment">
            <span class="ci-legend__dot" [style.background]="s.color"></span>
            {{ 'pages.organisation.credits.info.seg.' + s.label | translate }}
            <strong>{{ s.credits | number: '1.0-0' }}</strong>
          </span>
        </div>

        <p class="ci-hint-impulse">
          {{
            'pages.organisation.credits.info.impulseNote'
              | translate: { per: impulsesPerLearner, count: (impulseCount | number) }
          }}
        </p>
        <p class="ci-hint-impulse" *ngIf="courseSetupCredits > 0">
          {{
            'pages.organisation.credits.info.setupNote'
              | translate
                : {
                    credits: (courseSetupCredits | number: '1.0-0'),
                    eur: (eurOf(courseSetupCredits) | number: '1.2-2')
                  }
          }}
        </p>

        <!-- Plan comparison -->
        <ng-container *ngIf="includedCredits !== null">
          <div class="ci-plan">
            <div class="ci-plan__head">
              <span>{{ 'pages.organisation.credits.info.planIncluded' | translate }}</span>
              <strong>{{ includedCredits | number: '1.0-0' }}</strong>
            </div>
            <nb-progress-bar
              [value]="planUsedPercent || 0"
              [status]="withinPlan ? 'success' : 'warning'"
              size="tiny"
            ></nb-progress-bar>
          </div>

          <p class="ci-verdict ci-verdict--ok" *ngIf="withinPlan">
            <nb-icon icon="checkmark-circle-2-outline"></nb-icon>
            {{ 'pages.organisation.credits.info.withinPlan' | translate }}
          </p>
          <p class="ci-verdict ci-verdict--over" *ngIf="!withinPlan">
            <nb-icon icon="alert-triangle-outline"></nb-icon>
            {{
              'pages.organisation.credits.info.overPlan'
                | translate
                  : {
                      credits: (overageCredits | number: '1.0-0'),
                      eur: (overageEur | number: '1.2-2')
                    }
            }}
          </p>
        </ng-container>

        <p class="ci-verdict ci-verdict--muted" *ngIf="includedCredits === null">
          {{ 'pages.organisation.credits.info.noPlanBudget' | translate }}
        </p>
      </section>

      <!-- 5. Price list: every priced activity, collapsed by default -->
      <section class="tile-flat">
        <header class="tile-flat__header">
          <button
            type="button"
            class="ci-prices-toggle"
            [attr.aria-expanded]="pricesOpen"
            (click)="pricesOpen = !pricesOpen"
          >
            <h5>{{ 'pages.organisation.credits.info.priceTitle' | translate }}</h5>
            <nb-icon [icon]="pricesOpen ? 'chevron-up-outline' : 'chevron-down-outline'"></nb-icon>
          </button>
        </header>
        <ng-container *ngIf="pricesOpen">
          <div class="ci-price-group" *ngFor="let g of priceGroups">
            <div class="ci-price-group__title">
              {{ 'pages.organisation.credits.info.priceGroup.' + g.key | translate }}
            </div>
            <div class="ci-prices">
              <div class="ci-price" *ngFor="let p of g.rows">
                <span class="ci-price__label">{{ p.label }}</span>
                <span class="ci-price__value">{{ p.credits | number: '1.0-2' }}</span>
              </div>
            </div>
          </div>
          <p class="ci-note">{{ 'pages.organisation.credits.info.priceFactorNote' | translate }}</p>
          <p class="ci-note">{{ 'pages.organisation.credits.info.priceNote' | translate }}</p>
        </ng-container>
      </section>
    </ng-container>
  </nb-card-body>

  <nb-card-footer>
    <button nbButton status="primary" type="button" (click)="close()">
      {{ 'buttons.close' | translate }}
    </button>
  </nb-card-footer>
</nb-card>
